import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Textarea } from '../components/ui/Textarea';
import { CameraIcon, TrashIcon, LinkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import apiClient from '../utils/api';
import API_CONFIG from '../config/api';
import { useFitbitStatus } from '../hooks/useFitbitStatus';
import { useDeviceStatus } from '../hooks/useDeviceStatus';
import { useTheme } from '../hooks/useTheme';
import { formatDateTime } from '../utils/dateFormatter';
import { TimezoneCombobox } from '../components/TimezonePicker';
import TargetsEditor from '../components/twin/TargetsEditor';
import ReminderSettings from '../components/settings/ReminderSettings';

// Height/weight are STORED in metric (cm/kg) so the twin's seed ranges and
// targets stay unit-consistent; we convert only for display and entry in the
// user's preferred unit. These helpers take an `imperial` flag (lb/in).
const KG_PER_LB = 0.45359237;
const CM_PER_IN = 2.54;
const r1 = (n) => Math.round(n * 10) / 10;
const r2 = (n) => Math.round(n * 100) / 100;
const kgToDisplay = (kg, imperial) => (kg === '' || kg == null ? '' : r1(imperial ? kg / KG_PER_LB : kg));
const cmToDisplay = (cm, imperial) => (cm === '' || cm == null ? '' : r1(imperial ? cm / CM_PER_IN : cm));
const displayToKg = (v, imperial) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : r2(imperial ? n * KG_PER_LB : n);
};
const displayToCm = (v, imperial) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : r1(imperial ? n * CM_PER_IN : n);
};

const MePage = () => {
  const { user, refreshUser, loading: authLoading, logout } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const { fitbitStatus, loading: loadingFitbit, refetch: refetchFitbitStatus } = useFitbitStatus();
  const { deviceStatus: ouraStatus, loading: loadingOura, refetch: refetchOura } = useDeviceStatus('oura');
  const [disconnectingDevice, setDisconnectingDevice] = useState(null);
  const { theme, toggleTheme } = useTheme();


  // Personal stats state
  const [statsActiveTab, setStatsActiveTab] = useState('demographics');
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsSaving, setStatsSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [formData, setFormData] = useState({
    age: '', sex: '', height: '', weight: '', ethnicity: '', location: '',
    chronic_conditions: [], medications: [], allergies: [],
    dietary_preferences: '', activity_level: '', sleep_hours: '',
    primary_goal: '', target_weight: '', daily_calorie_target: '',
    mood_logs: [],
  });

  const statsTabs = [
    { id: 'demographics', label: 'Demographics' },
    { id: 'medical', label: 'Medical' },
    { id: 'lifestyle', label: 'Lifestyle' },
    { id: 'goals', label: 'Goals' },
    { id: 'mental-health', label: 'Mental Health' },
  ];

  // ── Profile helpers ──

  const formattedCreatedAt = useMemo(() => {
    return user?.created_at ? formatDateTime(user.created_at) : '—';
  }, [user?.created_at]);

  const getProfilePictureUrl = useMemo(() => {
    if (user?.profile_picture) {
      return `${API_CONFIG.UPLOADS_BASE_URL}/profile_pictures/${user.profile_picture}`;
    }
    return null;
  }, [user?.profile_picture]);

  const validateRedirectUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const allowedDomains = ['www.fitbit.com', 'accounts.fitbit.com'];
      return allowedDomains.includes(urlObj.hostname);
    } catch {
      return false;
    }
  };

  const handleConnectFitbit = async () => {
    try {
      const response = await apiClient.get('/api/auth/fitbit/connect');
      if (response.status === 200) {
        const redirectUrl = response.data.redirect_url;
        if (!validateRedirectUrl(redirectUrl)) {
          toast.error('Invalid redirect URL received from server');
          return;
        }
        window.location.href = redirectUrl;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate Fitbit connection');
    }
  };

  const handleDisconnectFitbit = async () => {
    setDisconnecting(true);
    try {
      const response = await apiClient.post('/api/auth/fitbit/disconnect', {});
      if (response.status === 200) {
        toast.success('Fitbit account disconnected successfully');
        await refetchFitbitStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect Fitbit account');
    } finally {
      setDisconnecting(false);
    }
  };

  // Generic device-layer connect/disconnect (Oura and future providers).
  // Fitbit keeps its own handlers above until it's migrated onto this layer.
  const DEVICE_ALLOWED_HOSTS = {
    oura: ['cloud.ouraring.com'],
  };

  const handleConnectDevice = async (provider) => {
    try {
      const response = await apiClient.get(`/api/auth/device/${provider}/connect`);
      if (response.status === 200) {
        const redirectUrl = response.data.redirect_url;
        const allowed = DEVICE_ALLOWED_HOSTS[provider] || [];
        let host;
        try {
          host = new URL(redirectUrl).hostname;
        } catch {
          toast.error('Invalid redirect URL received from server');
          return;
        }
        if (!allowed.includes(host)) {
          toast.error('Invalid redirect URL received from server');
          return;
        }
        window.location.href = redirectUrl;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to initiate ${provider} connection`);
    }
  };

  const handleDisconnectDevice = async (provider, refetch) => {
    setDisconnectingDevice(provider);
    try {
      const response = await apiClient.post(`/api/auth/device/${provider}/disconnect`, {});
      if (response.status === 200) {
        toast.success('Device disconnected successfully');
        await refetch();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to disconnect ${provider}`);
    } finally {
      setDisconnectingDevice(null);
    }
  };

  const formatConnectedDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    try {
      const response = await apiClient.post('/api/auth/profile-picture', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.status === 200) {
        toast.success('Profile picture uploaded!');
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload profile picture');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteProfilePicture = async () => {
    try {
      const response = await apiClient.delete('/api/auth/profile-picture');
      if (response.status === 200) {
        toast.success('Profile picture deleted!');
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete profile picture');
    }
  };

  // ── Units ──
  // formData.height/weight/target_weight hold DISPLAY values in the current
  // unit; a ref carries the unit into the []-dep fetch callback without stale
  // closures, and toggling reinterprets the in-box numbers into the new unit.
  const isImperial = (user?.weight_unit || 'kg') === 'lb';
  const weightUnitLabel = isImperial ? 'lb' : 'kg';
  const heightUnitLabel = isImperial ? 'in' : 'cm';
  const unitRef = useRef(user?.weight_unit || 'kg');
  useEffect(() => {
    unitRef.current = user?.weight_unit || 'kg';
  }, [user?.weight_unit]);

  const handleWeightUnitChange = async (newUnit) => {
    const cur = user?.weight_unit || 'kg';
    if (newUnit === cur) return;
    const curImp = cur === 'lb';
    const newImp = newUnit === 'lb';
    // Reinterpret the in-box numbers into the new unit (round-trip via metric).
    setFormData((prev) => ({
      ...prev,
      height: cmToDisplay(displayToCm(prev.height, curImp), newImp),
      weight: kgToDisplay(displayToKg(prev.weight, curImp), newImp),
      target_weight: kgToDisplay(displayToKg(prev.target_weight, curImp), newImp),
    }));
    unitRef.current = newUnit;
    try {
      await apiClient.patch('/api/auth/weight-unit', { weight_unit: newUnit });
      await refreshUser();
    } catch {
      toast.error('Failed to update weight unit');
    }
  };

  // ── Personal stats helpers ──

  const fetchPersonalStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await apiClient.get('/api/personal-stats/');
      if (response.status === 200) {
        const data = response.data;
        setStats(data);
        const imp = unitRef.current === 'lb';
        setFormData({
          age: data.age || '', sex: data.sex || '',
          height: cmToDisplay(data.height, imp), weight: kgToDisplay(data.weight, imp),
          ethnicity: data.ethnicity || '', location: data.location || '',
          chronic_conditions: data.chronic_conditions || [],
          medications: data.medications || [],
          allergies: data.allergies || [],
          dietary_preferences: data.dietary_preferences || '',
          activity_level: data.activity_level || '',
          sleep_hours: data.sleep_hours || '',
          primary_goal: data.primary_goal || '',
          target_weight: kgToDisplay(data.target_weight, imp),
          daily_calorie_target: data.daily_calorie_target || '',
          mood_logs: data.mood_logs || [],
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to fetch personal stats');
      }
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPersonalStats();
  }, [fetchPersonalStats]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const addArrayItem = (field) => {
    setFormData((prev) => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const addMoodLog = () => {
    setFormData((prev) => ({
      ...prev,
      mood_logs: [...prev.mood_logs, { date: '', mood: '' }],
    }));
  };

  const handleMoodLogChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      mood_logs: prev.mood_logs.map((log, i) =>
        i === index ? { ...log, [field]: value } : log
      ),
    }));
  };

  const removeMoodLog = (index) => {
    setFormData((prev) => ({
      ...prev,
      mood_logs: prev.mood_logs.filter((_, i) => i !== index),
    }));
  };

  const handleStatsSubmit = async (e) => {
    e.preventDefault();
    // No required fields — a partial profile is allowed and still improves the
    // twin. Empty numeric fields save as null; height/weight convert to metric.
    setStatsSaving(true);
    const imp = unitRef.current === 'lb';
    try {
      const method = stats ? 'put' : 'post';
      const response = await apiClient[method]('/api/personal-stats/', {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        height: displayToCm(formData.height, imp),
        weight: displayToKg(formData.weight, imp),
        sleep_hours: formData.sleep_hours ? parseFloat(formData.sleep_hours) : null,
        target_weight: displayToKg(formData.target_weight, imp),
        daily_calorie_target: formData.daily_calorie_target ? parseFloat(formData.daily_calorie_target) : null,
        chronic_conditions: formData.chronic_conditions.filter((item) => item.trim()),
        medications: formData.medications.filter((item) => item.trim()),
        allergies: formData.allergies.filter((item) => item.trim()),
        mood_logs: formData.mood_logs.filter((log) => log.date && log.mood),
      });
      if (response.status === 200 || response.status === 201) {
        toast.success(stats ? 'Personal stats updated!' : 'Personal stats created!');
        await fetchPersonalStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save personal stats');
    } finally {
      setStatsSaving(false);
    }
  };

  // ── Render helpers for stats tabs ──

  const renderUnitToggle = () => (
    <div className="flex items-center gap-3">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Units</span>
      <div className="flex gap-2">
        {[['kg', 'kg / cm'], ['lb', 'lb / in']].map(([u, lbl]) => (
          <button
            type="button"
            key={u}
            onClick={() => handleWeightUnitChange(u)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
              (user?.weight_unit || 'kg') === u
                ? 'bg-solar-gradient text-[#1a1200] border-primary-500'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );

  const renderDemographicsTab = () => (
    <div className="space-y-6">
      {renderUnitToggle()}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Age</label>
          <Input type="number" value={formData.age} onChange={(e) => handleInputChange('age', e.target.value)} placeholder="Enter your age" min="0" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sex</label>
          <Select value={formData.sex} onChange={(e) => handleInputChange('sex', e.target.value)}>
            <option value="">Select sex</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="intersex">Intersex</option>
            <option value="other">Other</option>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Height ({heightUnitLabel})</label>
          <Input type="number" value={formData.height} onChange={(e) => handleInputChange('height', e.target.value)} placeholder={`Enter your height in ${heightUnitLabel}`} min="0" step="0.1" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Weight ({weightUnitLabel})</label>
          <Input type="number" value={formData.weight} onChange={(e) => handleInputChange('weight', e.target.value)} placeholder={`Enter your weight in ${weightUnitLabel}`} min="0" step="0.1" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ethnicity</label>
          <Input type="text" value={formData.ethnicity} onChange={(e) => handleInputChange('ethnicity', e.target.value)} placeholder="Enter your ethnicity" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
          <Input type="text" value={formData.location} onChange={(e) => handleInputChange('location', e.target.value)} placeholder="Enter your location" />
        </div>
      </div>
    </div>
  );

  const renderArrayField = (field, label, placeholder) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
      {formData[field].map((item, index) => (
        <div key={index} className="flex gap-2 mb-2">
          <Input value={item} onChange={(e) => handleArrayChange(field, index, e.target.value)} placeholder={placeholder} />
          <Button type="button" variant="destructive" size="sm" onClick={() => removeArrayItem(field, index)}>
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem(field)} className="flex items-center">
        <PlusIcon className="w-4 h-4 mr-2" />
        Add {label.replace(/s$/, '')}
      </Button>
    </div>
  );

  const renderMedicalTab = () => (
    <div className="space-y-6">
      {renderArrayField('chronic_conditions', 'Chronic Conditions', 'Enter chronic condition')}
      {renderArrayField('medications', 'Medications', 'Enter medication')}
      {renderArrayField('allergies', 'Allergies', 'Enter allergy')}
    </div>
  );

  const renderLifestyleTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Dietary Preferences</label>
        <Textarea value={formData.dietary_preferences} onChange={(e) => handleInputChange('dietary_preferences', e.target.value)} placeholder="Describe your dietary preferences" rows={3} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Activity Level</label>
        <Select value={formData.activity_level} onChange={(e) => handleInputChange('activity_level', e.target.value)}>
          <option value="">Select activity level</option>
          <option value="sedentary">Sedentary</option>
          <option value="light">Light</option>
          <option value="moderate">Moderate</option>
          <option value="high">High</option>
        </Select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sleep Hours (per night)</label>
        <Input type="number" value={formData.sleep_hours} onChange={(e) => handleInputChange('sleep_hours', e.target.value)} placeholder="Enter average sleep hours" min="0" max="24" step="0.5" />
      </div>
    </div>
  );

  const renderGoalsTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Primary Goal</label>
        <Textarea value={formData.primary_goal} onChange={(e) => handleInputChange('primary_goal', e.target.value)} placeholder="Describe your primary health goal" rows={3} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Target Weight ({weightUnitLabel})</label>
        <Input type="number" value={formData.target_weight} onChange={(e) => handleInputChange('target_weight', e.target.value)} placeholder={`Enter your target weight in ${weightUnitLabel}`} min="0" step="0.1" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Daily Calorie Target (kcal)</label>
        <Input type="number" value={formData.daily_calorie_target} onChange={(e) => handleInputChange('daily_calorie_target', e.target.value)} placeholder="Enter your daily calorie target" min="0" step="50" />
      </div>
    </div>
  );

  const renderMentalHealthTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mood Logs</label>
        {formData.mood_logs.map((log, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <Input type="date" value={log.date} onChange={(e) => handleMoodLogChange(index, 'date', e.target.value)} />
            <Input value={log.mood} onChange={(e) => handleMoodLogChange(index, 'mood', e.target.value)} placeholder="How are you feeling?" />
            <Button type="button" variant="destructive" size="sm" onClick={() => removeMoodLog(index)}>
              <TrashIcon className="w-4 h-4" />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addMoodLog} className="flex items-center">
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Mood Log
        </Button>
      </div>
    </div>
  );

  const renderStatsTabContent = () => {
    switch (statsActiveTab) {
      case 'demographics': return renderDemographicsTab();
      case 'medical': return renderMedicalTab();
      case 'lifestyle': return renderLifestyleTab();
      case 'goals': return renderGoalsTab();
      case 'mental-health': return renderMentalHealthTab();
      default: return null;
    }
  };

  // ── Loading ──

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface dark:bg-surface-dark">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">My Profile</h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">Manage your account and health information</p>
      </div>

      {/* Profile Section */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar */}
            <div className="flex flex-col items-center space-y-3">
              <div className="w-24 h-24 rounded-full bg-solar-gradient shadow-glow-sm flex items-center justify-center text-[#1a1200] text-3xl font-semibold overflow-hidden">
                {getProfilePictureUrl ? (
                  <img src={getProfilePictureUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span>{user?.email?.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="flex space-x-2">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex items-center">
                  <CameraIcon className="w-4 h-4 mr-1" />
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
                {user?.profile_picture && (
                  <Button variant="destructive" size="sm" onClick={handleDeleteProfilePicture} className="flex items-center">
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{user?.email || '—'}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Member since {formattedCreatedAt}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connected Devices */}
      <Card>
        <CardHeader>
          <CardTitle>Connected Devices</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {fitbitStatus.connected ? (
                  <CheckCircleIcon className="w-6 h-6 text-green-500" />
                ) : (
                  <LinkIcon className="w-6 h-6 text-gray-400" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Fitbit</p>
                {fitbitStatus.connected ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Connected {formatConnectedDate(fitbitStatus.connected_at)}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Not connected</p>
                )}
              </div>
            </div>
            <div>
              {loadingFitbit ? (
                <Button variant="outline" size="sm" disabled>Loading...</Button>
              ) : fitbitStatus.connected ? (
                <Button
                  variant="outline" size="sm"
                  onClick={handleDisconnectFitbit}
                  disabled={disconnecting}
                  className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={handleConnectFitbit}>Connect</Button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between p-4 mt-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {ouraStatus.connected ? (
                  <CheckCircleIcon className="w-6 h-6 text-green-500" />
                ) : (
                  <LinkIcon className="w-6 h-6 text-gray-400" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Oura Ring</p>
                {ouraStatus.connected ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Connected {formatConnectedDate(ouraStatus.connected_at)}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Not connected</p>
                )}
              </div>
            </div>
            <div>
              {loadingOura ? (
                <Button variant="outline" size="sm" disabled>Loading...</Button>
              ) : ouraStatus.connected ? (
                <Button
                  variant="outline" size="sm"
                  onClick={() => handleDisconnectDevice('oura', refetchOura)}
                  disabled={disconnectingDevice === 'oura'}
                  className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  {disconnectingDevice === 'oura' ? 'Disconnecting...' : 'Disconnect'}
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => handleConnectDevice('oura')}>Connect</Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card>
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <TimezoneCombobox />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Weight Unit</label>
            <div className="flex gap-2">
              {['kg', 'lb'].map((u) => (
                <button
                  key={u}
                  onClick={() => handleWeightUnitChange(u)}
                  className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    (user?.weight_unit || 'kg') === u
                      ? 'bg-solar-gradient text-[#1a1200] border-primary-500'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Theme</label>
            <div className="flex gap-2">
              {[['light', 'Light'], ['dark', 'Dark']].map(([val, lbl]) => (
                <button
                  key={val}
                  onClick={() => { if (theme !== val) toggleTheme(); }}
                  className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    theme === val
                      ? 'bg-solar-gradient text-[#1a1200] border-primary-500'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Personal Health Profile */}
      <Card>
        <CardHeader>
          <CardTitle>Personal Health Profile</CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This information helps personalize your health insights. Fill in what
            you can — a partial profile is fine, and every field sharpens your twin.
            You can also just tell the logger on your home page ("I'm 34", "my goal is
            to sleep better") and it'll fill these in for you.
          </p>
        </CardHeader>
        <CardContent>
          {statsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {/* Tab Navigation */}
              <div className="flex flex-wrap border-b border-gray-200 dark:border-gray-700 mb-6">
                {statsTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setStatsActiveTab(tab.id)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      statsActiveTab === tab.id
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <form onSubmit={handleStatsSubmit}>
                {renderStatsTabContent()}
                <div className="mt-8 flex justify-center">
                  <Button type="submit" disabled={statsSaving} className="px-8 py-3">
                    {statsSaving ? 'Saving...' : stats ? 'Update Stats' : 'Create Stats'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </CardContent>
      </Card>

      {/* Twin Ultimate Targets */}
      <TargetsEditor />

      {/* Log reminders (push notifications) */}
      <ReminderSettings />

      {/* Account Actions */}
      <Card>
        <CardContent className="p-6">
          <Button variant="outline" onClick={logout} className="text-red-600 hover:text-red-700 dark:text-red-400">
            Sign Out
          </Button>
        </CardContent>
      </Card>

    </div>
  );
};

export default MePage;
